'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTheme } from '@/contexts/ThemeContext';
import { Film, Tv, TrendingUp, Sparkles } from 'lucide-react';

// Full-screen / dashboard-style routes that shouldn't show the marketing footer.
const HIDDEN_PREFIXES = ['/auth', '/admin', '/epify-admin', '/verify-email'];

const BROWSE_LINKS = [
  { label: 'Trending', href: '/browse/trending', icon: TrendingUp },
  { label: 'Popular', href: '/browse/popular', icon: Sparkles },
  { label: 'Movies', href: '/browse/movie', icon: Film },
  { label: 'TV Shows', href: '/browse/tv', icon: Tv },
];

const ACCOUNT_LINKS = [
  { label: 'My Watchlist', href: '/dashboard' },
  { label: 'Profile', href: '/profile' },
  { label: 'Settings', href: '/settings' },
];

const COMPANY_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Search', href: '/search' },
];

export function Footer() {
  const pathname = usePathname();
  const { activeTheme, loading: themeLoading } = useTheme();
  const logoSrc = themeLoading ? '/image/epify.png' : activeTheme?.logo?.url || '/image/epify.png';
  const year = new Date().getFullYear();

  if (pathname && HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  // Marketing stats are landing-page content only — hide them on inner/detail pages.
  const isLandingPage = pathname === '/' || pathname === '/design-preview';

  return (
    <footer className="relative mt-12 md:mt-16 border-t border-white/10 bg-black">
      {/* Soft red glow accent to match the cinematic look */}
      <div className="pointer-events-none absolute -top-px left-1/2 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-red-600/50 to-transparent" />

      <div className="w-full px-5 md:px-10 py-10 md:py-14">
        <div className="grid grid-cols-2 gap-8 md:gap-10 md:grid-cols-4 lg:grid-cols-5">
          {/* Brand + tagline */}
          <div className="col-span-2 lg:col-span-2">
            <Link href="/" className="inline-flex items-center transition-opacity hover:opacity-80">
              <Image src={logoSrc} alt="Epify" width={150} height={44} className="h-9 w-auto" />
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-gray-400">
              Your ultimate companion for tracking movies and TV shows. Never miss a new season or
              episode again.
            </p>
            {/* Stats — landing page only */}
            {isLandingPage && (
              <div className="mt-6 flex gap-8">
                {[
                  { value: '10K+', label: 'Movies & Shows' },
                  { value: '24/7', label: 'Updates' },
                  { value: 'Free', label: 'Forever' },
                ].map((s) => (
                  <div key={s.label}>
                    <div className="text-lg font-bold text-white">{s.value}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Browse */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-300">
              Browse
            </h3>
            <ul className="space-y-2.5">
              {BROWSE_LINKS.map(({ label, href, icon: Icon }) => (
                <li key={href}>
                  <Link
                    href={href}
                    className="group inline-flex items-center gap-2 text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    <Icon className="h-3.5 w-3.5 text-gray-600 transition-colors group-hover:text-red-500" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Account */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-300">
              Account
            </h3>
            <ul className="space-y-2.5">
              {ACCOUNT_LINKS.map(({ label, href }) => (
                <li key={href}>
                  <Link href={href} className="text-sm text-gray-400 transition-colors hover:text-white">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Explore */}
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-300">
              Explore
            </h3>
            <ul className="space-y-2.5">
              {COMPANY_LINKS.map(({ label, href }) => (
                <li key={href}>
                  <Link href={href} className="text-sm text-gray-400 transition-colors hover:text-white">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row">
          <p className="text-xs text-gray-500">© {year} Epify. Built for entertainment enthusiasts.</p>
          {/* TMDB attribution — kept clearly visible and linked per TMDB API terms. */}
          <a
            href="https://www.themoviedb.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs text-gray-300 hover:text-white transition-colors"
          >
            <span>Powered by</span>
            {/* Plain img: Next/Image won't render SVGs (dangerouslyAllowSVG is off). */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/image/TMDB.svg" alt="The Movie Database (TMDB)" width={100} height={20} className="h-5 w-auto" />
          </a>
        </div>
      </div>
    </footer>
  );
}
